import type { ReactNode } from 'react';

export interface FieldFrameProps {
  id: string;
  label: string;
  /** "Why we ask" copy. Plain language, no jargon. */
  hint?: string;
  error?: string;
  children: ReactNode;
}

/**
 * Shared label / hint / error scaffolding. Hints are grey-600, which measures
 * 7.08:1 on white; grey-400 and grey-500 are border and large-text colours in
 * this palette and are never body copy.
 */
export default function FieldFrame({ id, label, hint, error, children }: FieldFrameProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="t-h3 text-ink">
        {label}
      </label>
      {hint && (
        <p id={hintId} className="t-meta measure text-grey-600">
          {hint}
        </p>
      )}
      {children}
      {error && (
        <p id={errorId} className="t-meta font-semibold text-alert" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Wires aria-describedby the same way for every control in this file's family. */
export function describedBy(id: string, hint?: string, error?: string): string | undefined {
  const ids = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

export const controlBase =
  'min-h-tap w-full rounded-sm border bg-white px-4 text-[1.0625rem] text-ink ' +
  'placeholder:text-grey-500 focus:border-green-600';
